import { useArtistTracksQuery } from '~/modules/artist-roster/hooks/useArtistTracksQuery';
import { useArtistQuery } from '~/modules/artist-roster/pages/roster/hooks/useArtistQuery';
import type { ArtistType } from '~/modules/artist-roster/types';

function getArtistInfo(label: string, artist: ArtistType | undefined) {
  const { partyId, uaid, name } = artist || {};

  return `[${label}: partyId[${partyId}], uaid[${uaid}], name[${name || 'No artist'}]`;
}

/**
 * Page used to test the API calls that are made in the existing artist-page e2e
 * test. The page will be used in a separate e2e test. The goal is to replicate
 * all the calls from the original test to see if there are differences in failure
 * between the full test and the API only test. If there are no differences, then
 * it probably means there is an issue in the environment where the tests are run...
 */
export function E2eArtistPage() {
  const header = 'e2e Artist Page';

  // Load the first page of artist data.
  const { data: firstPageData, isFetching: fetchingFirstPage } = useArtistQuery(
    {
      labelIds: [],
      isCrossover: false,
      pageNumber: 1,
    },
  );

  // Search for David Guetta (there should only be one).
  const { data: searchResults, isFetching: fetchingSearchResults } =
    useArtistQuery({
      searchText: 'david guetta',
      labelIds: [],
      isCrossover: false,
      pageNumber: 1,
    });

  // Now make the individual calls from the Artist Details Page for David Guetta.
  const davidGuetta = searchResults?.items[0];

  // Two calls from <TabList />...
  // 1) Released tracks info.
  const { data: tracksData, isFetching: isFetchingReleased } =
    useArtistTracksQuery({
      uaid: davidGuetta?.uaid,
      pageNumber: 1,
      limit: 1,
      isReleased: true,
    });

  // 2) Pre-released tracks info.
  const { data: prereleasedData, isFetching: isFetchingPreRelease } =
    useArtistTracksQuery({
      uaid: davidGuetta?.uaid,
      pageNumber: 1,
      limit: 1,
      isReleased: false,
    });

  // 3) One call from <TrackListHeader />.
  const { isFetching: isFetchingHeader } = useArtistTracksQuery({
    uaid: davidGuetta?.uaid,
    columnSort: { id: 'initialReleaseDate', desc: true },
    pageNumber: 1,
    limit: 1,
    isReleased: true,
  });

  // 4) One call from <TrackList />.
  const { isFetching: isFetchingTracks } = useArtistTracksQuery({
    uaid: davidGuetta?.uaid,
    columnSort: { id: 'initialReleaseDate', desc: true },
    pageNumber: 1,
    isReleased: true,
  });

  // Now make the individual calls when searching for the track Titanium.

  // 1) One call for the track count.
  const { isFetching: isTrackSearchCountLoading } = useArtistTracksQuery({
    uaid: davidGuetta?.uaid,
    columnSort: { id: 'initialReleaseDate', desc: true },
    limit: 1,
    pageNumber: 1,
    isReleased: true,
    searchText: 'titanium',
  });

  // 2) One call for the actual search results.
  const { isFetching: isTrackSearchResultsLoading } = useArtistTracksQuery({
    uaid: davidGuetta?.uaid,
    columnSort: { id: 'initialReleaseDate', desc: true },
    limit: 20,
    pageNumber: 1,
    isReleased: true,
    searchText: 'titanium',
  });

  return (
    <div className="flex flex-col gap-sm p-sm">
      <h1>{header}</h1>

      <span>
        {fetchingFirstPage
          ? 'Loading...'
          : getArtistInfo('First Page Data', firstPageData?.items[0])}
      </span>

      <span>
        {fetchingSearchResults
          ? 'Loading...'
          : getArtistInfo('Search Data', davidGuetta)}
      </span>

      <span>
        {isFetchingReleased
          ? 'Loading...'
          : `Released Tracks Count [${tracksData?.pagination.total || 0}]`}
      </span>

      <span>
        {isFetchingPreRelease
          ? 'Loading...'
          : `Pre-Release Tracks Count [${prereleasedData?.pagination.total || 0}]`}
      </span>

      <span>{isFetchingHeader ? 'Loading...' : 'Header Loaded'}</span>

      <span>{isFetchingTracks ? 'Loading...' : 'Tracks Loaded'}</span>

      <span>
        {isTrackSearchCountLoading
          ? 'Loading...'
          : 'Titanium search count Loaded'}
      </span>

      <span>
        {isTrackSearchResultsLoading
          ? 'Loading...'
          : 'Titanium search results Loaded'}
      </span>
    </div>
  );
}

E2eArtistPage.displayName = 'E2eArtistPage';
